import axios from 'axios';
import { Config } from 'config';
import { getAccessToken } from 'lib/Helper/Auth';
import { IntraMerges, Merge, MergePreview, PagedMerges } from 'lib/Model/Merge';
import { PagedRelations, Relation } from 'lib/Model/Relation';
import { Suggestion, PagedSuggestions } from 'lib/Model/Suggestion';

export function fetchSuggestionsForCompany(
  companyId: string,
  pageNumber: number,
  itemsPerPage: number,
): Promise<PagedSuggestions> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/companies/${companyId}/suggestions`, {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
      },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return {
        total_count: response.data.total_count,
        data: response.data.data as Suggestion[],
      };
    });
}

export function fetchMergesForCompany(
  companyId: string,
  pageNumber: number,
  itemsPerPage: number,
): Promise<PagedMerges> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/companies/${companyId}/merges`, {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
      },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return {
        total_count: response.data.total_count,
        data: response.data.data as Merge[],
      };
    });
}

export function fetchRelationsForCompany(
  companyId: string,
  pageNumber: number,
  itemsPerPage: number,
): Promise<PagedRelations> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/companies/${companyId}/relations`, {
      params: {
        page: pageNumber,
        page_size: itemsPerPage,
      },
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      return {
        total_count: response.data.total_count,
        data: response.data.data as Relation[],
      };
    });
}

export function actionSuggestion(
  companyId: string,
  suggestionId: number,
  action: 'merge' | 'related',
  force: boolean = false,
  aliasId?: string,
): Promise<boolean> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/companies/${companyId}/merges`,
      {
        status: action,
        suggestion_id: suggestionId,
        company_id: companyId,
        force: force,
        // Lets the server reject the action if the suggestion id no longer
        // points at the company shown in this tab (nightly regeneration
        // reassigns suggestion ids).
        alias_id: aliasId,
      },
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then((response) => {
      return true;
    });
}

export function deleteMerge(
  companyId: string,
  mergeId: string,
): Promise<boolean> {
  return axios
    .delete(
      `${Config.API_BASE_URL}intranet/companies/${companyId}/merges/${mergeId}`,
      {
        data: {
          merge_id: mergeId,
          parent_id: companyId,
        },
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then((response) => {
      return true;
    });
}

/**
 * Detaches a specific company from its merge cluster. Unlike deleteMerge,
 * this targets the clicked company itself rather than a merge row (whose
 * alias may be a different company).
 */
export function detachClusterMember(
  companyId: string,
  memberId: string,
): Promise<boolean> {
  return axios
    .delete(
      `${Config.API_BASE_URL}intranet/companies/${companyId}/cluster-members/${memberId}`,
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then((response) => {
      return true;
    });
}

export function fetchIntraMergesForCompany(
  companyId: string,
): Promise<IntraMerges[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/companies/${companyId}/intramerges`, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => response.data.data as IntraMerges[]);
}

export function exportIntramerges(): Promise<any> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/companies/intramerges/export`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    })
    .then((response) => {
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '');
      const formattedDate = date + time;
      const filename = `intra_merges_report${formattedDate}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    });
}

export function fetchMergePreview(
  companyId: string,
  suggestionId: number,
): Promise<MergePreview> {
  return axios
    .get(
      `${Config.API_BASE_URL}intranet/companies/${companyId}/merge_preview/${suggestionId}`,
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          'access-token': getAccessToken(),
        },
      },
    )
    .then((response) => response.data.data as MergePreview);
}
